"use client";
import { useState } from "react";
import type { ImageItemT } from "@/forApp/types/Entity";
import IconButton from "@/shared/Buttons/IconButton/IconButton";
import { Icons } from "@/shared/Icons/Icons";
import ImageWithLoader from "@/shared/Image/ImageWithLoader";
import FullscreenGallery from "./FullscreenGallery";
import styles from "./styles.module.css";

interface ImageGalleryProps {
	images: ImageItemT[];
	className?: string;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
	images,
	className = "",
}) => {
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);
	const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

	// If no images, return null
	if (!images || images.length === 0) {
		return null;
	}

	const selectedImage = images[selectedImageIndex];

	const handlePrevImage = () => {
		setSelectedImageIndex((prevIndex) =>
			prevIndex === 0 ? images.length - 1 : prevIndex - 1,
		);
	};

	const handleNextImage = () => {
		setSelectedImageIndex((prevIndex) =>
			prevIndex === images.length - 1 ? 0 : prevIndex + 1,
		);
	};

	const openFullscreen = () => {
		setIsFullscreenOpen(true);
	};

	const closeFullscreen = () => {
		setIsFullscreenOpen(false);
	};

	return (
		<>
			<div className={`${styles.galleryContainer} ${className}`}>
				<div className={styles.mainImageContainer}>
					<ImageWithLoader
						src={selectedImage.original_path}
						alt="Фото объекта"
						fill
						objectFit="none"
						borderRadius={0}
						className={styles.mainImage}
						priority
					/>

					{images.length > 1 && (
						<>
							<IconButton
								icon={<Icons.ArrowLeft />}
								onClick={handlePrevImage}
								color="gallery"
								className={styles.prevButton}
								ariaLabel="Previous image"
							/>

							<IconButton
								icon={<Icons.ArrowRight />}
								onClick={handleNextImage}
								color="gallery"
								className={styles.nextButton}
								ariaLabel="Next image"
							/>
						</>
					)}

					<IconButton
						icon={<Icons.Fullscreen />}
						onClick={openFullscreen}
						color="gallery"
						className={styles.fullscreenButton}
						ariaLabel="Полноэкранный режим"
					/>
				</div>

				<div className={styles.thumbnailsContainer}>
					{images.map((image, index) => (
						<div
							key={`${image.original_path}-${index}`}
							className={`${styles.thumbnailWrapper} ${index === selectedImageIndex ? styles.activeThumbnail : ""}`}
							onClick={() => setSelectedImageIndex(index)}
						>
							<ImageWithLoader
								src={image.thumbnail_path || image.original_path}
								alt={`Фото объекта ${index + 1}`}
								width={85}
								height={85}
								objectFit="cover"
								borderRadius={0}
								className={styles.thumbnailImage}
							/>
						</div>
					))}
				</div>
			</div>

			{isFullscreenOpen && (
				<FullscreenGallery
					images={images}
					initialIndex={selectedImageIndex}
					onClose={closeFullscreen}
				/>
			)}
		</>
	);
};

export default ImageGallery;
